import { useAccountStore } from '@components/Stores/accountStore';
import { useMaterialsStore } from '@components/Stores/MaterialsStore';
import React, { useState } from 'react';
import { getAllRoutes } from '../../sp-firebase/routesFB';
import { IRoute } from '../../sp-types';
import { useCardBar } from '../../components/FormulaCardBar/useCardBar';
import {
  addColor,
  getItemsOfMaterial,
  getStartDate,
  sliceFromDate,
} from '../Policy/MaterialDetails/components/MaterialDetailUtils';
import { MaterialRoutes } from '../Policy/MaterialDetails/components/MaterialRoutes';
import { ChartViewToolbar } from '../Policy/ProductOverview/ProductOverviewChart/ChartViewToolbar';
import { OpportunityChart } from './components/OpportunityChart';
import {
  getRoutePrice,
  IndexRoutes,
  isSameMonth,
  KeyPoints,
  OpportunityViewOptions,
  PricePoint,
  RouteExtraInfo,
} from './components/OpportunityUtils';
//import { default as rebar } from './data/rebar-opportunity.json';
import { default as rebar } from './data/rebar-routes.json';
import { useExposureStore, GENERAL_ID } from '../../components/Stores/exposureDataStore';
import { useCurrencyRates } from '@hooks/useCurrencyRates';

export const Opportunity: React.FC = () => {
  const { account, formulaColorDictionary: colorsMap } = useAccountStore();
  const { materialsMap } = useMaterialsStore();
  const {
    netExposure,
    currentSelectedFormulaId: materialId,
    setCurrentSelectedFormulaId: setMaterialId,
  } = useExposureStore();
  const { convertionMap } = useCurrencyRates();

  const [monthsToShow, setMonthsToShow] = useState<number>(12);
  const [selectedRoute, setSelectedRoute] = React.useState<IRoute | undefined>();
  const [allRoutes, setAllRoutes] = React.useState<IRoute[] | undefined>();
  const [showRoutes, setShowRoutes] = useState<
    { id: string; routeId: string; color: string; routeName: string; visible: boolean }[]
  >([]);

  React.useEffect(() => {
    if ((!materialId || materialId === GENERAL_ID) && account?.formulasToAccount?.length > 1) {
      setMaterialId(account.formulasToAccount[1].formulaId);
    }
  }, [account.formulasToAccount, materialId]);
  const [currentPrice, setCurrentPrice] = useState<IndexRoutes>({} as IndexRoutes);
  const [pricePoints, setPricePoints] = useState<PricePoint[]>([]);

  const currDate = new Date();
  const getVisibleData = (indexRoutes: IndexRoutes[], showCount: number, { price }: IndexRoutes): IndexRoutes[] =>
    sliceFromDate(indexRoutes, getStartDate(showCount)).map((indexPrice) =>
      (isSameMonth(new Date(indexPrice.date), currDate) && !indexPrice.price) || new Date(indexPrice.date) >= currDate
        ? { ...indexPrice, futurePrice: price }
        : indexPrice,
    );

  async function getRoutes() {
    setShowRoutes([]);

    const _allRoutes = await getAllRoutes();

    const materialRoutes = getItemsOfMaterial(_allRoutes || [], materialId).map(addColor);

    setAllRoutes(materialRoutes);

    setShowRoutes(
      materialRoutes.map(({ id, routeId, color, name }) => ({
        id,
        routeId: id || routeId || '',
        routeName: name,
        color,
        visible: true,
      })),
    );
  }

  React.useEffect(() => {
    if (materialId !== GENERAL_ID) {
      // temporary
      getRoutes();
      const currDateVal = rebar
        .filter(({ date }) => isSameMonth(new Date(date), currDate))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())?.[0].date;

      const currIndex = currDateVal ? rebar.findIndex(({ date }) => date === currDateVal) : 0;

      setCurrentPrice(rebar[currIndex]);

      onDataClick(rebar[currIndex + 2]);
    }
  }, [materialId]);

  const [pointPrices, setPointPrices] = React.useState<IndexRoutes | undefined>(); // confusing :-(
  const onDataClick = (priceData: IndexRoutes) => {
    setPointPrices(priceData);

    const updatedPoints = [
      {
        name: 'Index price',
        price: priceData.price || currentPrice.price || '',
        color: colorsMap[materialId],
        id: '',
        visible: true,
      },
      ...showRoutes.map(({ color, routeId, id, routeName, visible }) => ({
        color,
        name: routeName,
        id: routeId,
        price: getRoutePrice(priceData, id || routeId),
        visible,
      })),
    ];
    setPricePoints(updatedPoints);
  };

  const onRouteClick = (_routeId: string) => {
    setShowRoutes((routes) =>
      routes.map(({ visible, ...route }) => ({ ...route, visible: route.id === _routeId ? !visible : visible })),
    );
  };

  useCardBar({ includeGeneral: false });

  return (
    <div className="flex flex-col flex-1  h-full gap-3">
      {materialId && currentPrice ? (
        <div className="flex flex-col bg-white shadow-custom py-[0.5rem] gap-[1rem]  px-[1.5rem]  h-full">
          <div className="flex flex-1 flex-col">
            <h4 className="">Routes backtesting</h4>
            <div className="flex flex-row flex-1   gap-10  overflow-hidden">
              <div className="flex flex-col  flex-1">
                <ChartViewToolbar
                  viewOptions={OpportunityViewOptions}
                  selectedValue={monthsToShow}
                  onSelectionChange={setMonthsToShow}
                />
                <OpportunityChart
                  indexRoutesData={getVisibleData(rebar.reverse(), monthsToShow, currentPrice)}
                  showRoutes={showRoutes}
                  materialColor={colorsMap[materialId]}
                  onDataClick={onDataClick}
                  selectedRoute={selectedRoute?.id || ''}
                />
              </div>
              <div className="w-[11.5rem]">
                <KeyPoints routePrices={pricePoints} selectedRoute={selectedRoute?.id || ''} />
              </div>
            </div>
          </div>

          <div className="flex flex-col flex-1 overflow-hidden max-w-[90rem] ">
            <MaterialRoutes
              routes={allRoutes}
              selectedRoute={selectedRoute}
              setSelectedRoute={setSelectedRoute}
              materialId={materialId}
              isEdit={false}
              multiCheckbox={showRoutes.filter(({ visible }) => visible).map(({ id }) => id)}
              onCheck={onRouteClick}
              isLoading={false}
              hidePriceCorrelation
              materialsMap={materialsMap}
            >
              <RouteExtraInfo pointPrices={pointPrices} currentPrice={currentPrice} />
            </MaterialRoutes>
          </div>
        </div>
      ) : (
        <OpportunitySkeleton />
      )}
    </div>
  );
};

const OpportunitySkeleton: React.FC = () => <div>loading...</div>;
